import { useEffect, useState, useCallback, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import { UserModelsService, type UserModel } from '../services/userModelsService';
import { CheckCircle2, RefreshCw, Zap } from 'lucide-react';

export function ActiveModelsList() {
    const { user } = useUser();
    const [models, setModels] = useState<UserModel[]>([]);
    const [loading, setLoading] = useState(false);
    const userModelsService = useMemo(() => new UserModelsService(), []);

    const fetchModels = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const activeModels = await userModelsService.getUserActiveModels(user.id);
            setModels(activeModels);
        } catch (error) {
            console.error('Error fetching active models:', error);
        } finally {
            setLoading(false);
        }
    }, [user, userModelsService]);

    useEffect(() => {
        fetchModels();
    }, [fetchModels]);

    if (!user) return null;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <Zap className="w-5 h-5 text-yellow-500" />
                        Modelos Ativos no Roteador
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Estes são os modelos disponíveis para o sistema escolher automaticamente.
                    </p>
                </div>
                <button
                    onClick={fetchModels}
                    disabled={loading}
                    className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                    title="Atualizar lista"
                >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {models.length === 0 ? (
                <div className="p-6 text-center bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-800 border-dashed">
                    <p className="text-gray-500 dark:text-gray-400">
                        Nenhum modelo ativo. Adicione uma API Key acima para ativar os modelos.
                    </p>
                </div>
            ) : (
                <div className="grid gap-3">
                    {models.map((model) => (
                        <div
                            key={`${model.provider}-${model.model_name}`}
                            className="flex items-center justify-between p-3 bg-white dark:bg-[#030712] border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${model.tier === 'premium' ? 'bg-purple-500' :
                                    model.tier === 'balanced' ? 'bg-blue-500' : 'bg-green-500'
                                    }`} />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                                        {model.model_name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {model.provider} • {model.tier}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right">
                                    <p className="text-xs font-mono text-gray-600 dark:text-gray-300">
                                        ${model.cost_per_input_token.toFixed(8)} / in
                                    </p>
                                    <p className="text-xs font-mono text-gray-600 dark:text-gray-300">
                                        ${model.cost_per_output_token.toFixed(8)} / out
                                    </p>
                                </div>
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
